import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import MIHeader from '../MIHeader';
import { fmtNum, fmtMonth, fmtMonthShort, getLatestPeriod, fetchLatestFortnightBreakdown, formatFortnightRange } from '../utils';
import { generateStatewiseAllocationPDF } from '../shareExport';
import { Helmet } from 'react-helmet';
import { useTranslation } from '../../../hooks/useTranslation';
import { localizedStateName } from '../../../utils/localizedDisplayName';

const ShareIcon = ({ size = 16, color = '#0F2238' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
    <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
    <line x1="8.6" y1="10.5" x2="15.4" y2="6.5" /><line x1="8.6" y1="13.5" x2="15.4" y2="17.5" />
  </svg>
);

const fmtNotifDate = (dateStr) => {
  if (!dateStr) return null;
  return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' });
};

const StatewiseAllocationPage = () => {
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [month, setMonth] = useState(null);
  const [fortnight, setFortnight] = useState(null);
  const [rows, setRows] = useState([]);
  const [sharing, setSharing] = useState(false);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const latest = await getLatestPeriod();
        if (!latest) throw new Error('No data available');

        const [{ data: curRows, error: curErr }, { data: millStates, error: msErr }, fortnightBreakdown] = await Promise.all([
          supabase
            .from('mi_v_state_monthly')
            .select('canonical_state, total_allocated_mt, mills_active, mills_reporting')
            .eq('period_month', latest),
          // A plain join filtered to one month (~585 rows) - not the heavy
          // mi_v_mill_metrics aggregate, which times out when queried unfiltered
          // across all mills' full history.
          supabase.from('mi_v_release_orders_canonical').select('mill_id, canonical_state').eq('period_month', latest),
          fetchLatestFortnightBreakdown(),
        ]);
        if (curErr) throw curErr;
        if (msErr) throw msErr;

        // Aggregate the fortnight's per-mill lines up to per-state totals -
        // same approach as the district page, just grouped by state instead.
        const stateByMill = new Map((millStates || []).map((m) => [m.mill_id, m.canonical_state]));
        const fortnightByState = new Map();
        if (fortnightBreakdown) {
          fortnightBreakdown.byMill.forEach((qty, millId) => {
            const state = stateByMill.get(millId);
            if (!state) return;
            fortnightByState.set(state, (fortnightByState.get(state) || 0) + qty);
          });
        }

        const merged = (curRows || [])
          .map((r) => ({
            state: r.canonical_state,
            millsActive: r.mills_active || 0,
            millsReporting: r.mills_reporting || 0,
            aug: Number(r.total_allocated_mt),
            sep: fortnightByState.get(r.canonical_state) ?? 0,
          }))
          .sort((a, b) => b.sep - a.sep);

        if (!isMounted) return;
        setMonth(latest);
        setFortnight(fortnightBreakdown);
        setRows(merged);
      } catch (e) {
        if (isMounted) setError(e.message);
      } finally {
        if (isMounted) setLoading(false);
      }
    })();
    return () => { isMounted = false; };
  }, []);

  const handleSharePDF = async () => {
    setSharing(true);
    await generateStatewiseAllocationPDF({ rows, month, fortnight, action: 'share' });
    setSharing(false);
  };

  const totals = rows.reduce((acc, r) => ({
    sep: acc.sep + r.sep,
    aug: acc.aug + r.aug,
    millsActive: acc.millsActive + r.millsActive,
    millsReporting: acc.millsReporting + r.millsReporting,
  }), { sep: 0, aug: 0, millsActive: 0, millsReporting: 0 });
  const zeroMtCount = totals.millsReporting - totals.millsActive;

  const sepRangeLabel = fortnight ? (formatFortnightRange(fortnight.periodStart, fortnight.periodEnd, language) || fortnight.periodLabel) : null;

  return (
    <div className="min-h-screen" style={{ background: '#0F2238' }}>
      <Helmet>
        <title>{sepRangeLabel ? `Statewise Sugar Quota Allocation — ${sepRangeLabel} vs ${fmtMonthShort(month)} | SugarHathi` : `Statewise Sugar Quota Allocation | SugarHathi`}</title>
        <meta name="description" content={sepRangeLabel ? `State-wise sugar quota allocation, ${sepRangeLabel} fortnight vs ${fmtMonth(month)} (last complete month), with active factory counts. By SugarHathi.` : `State-wise sugar quota allocation with active factory counts, updated automatically. By SugarHathi.`} />
      </Helmet>
      <MIHeader subtitle={t('marketIntelligence.statewiseAllocation', 'Statewise allocation')} />
      <main className="min-h-[calc(100vh-64px)]" style={{ background: '#FDF8F0' }}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-2xl py-8">
          {loading && (
            <div className="flex flex-col items-center justify-center py-24" style={{ color: '#78716C' }}>
              <Icon name="Loader" size={32} className="animate-spin mb-3" /><p>{t('common.loading', 'Loading...')}</p>
            </div>
          )}
          {!loading && error && (
            <div className="flex flex-col items-center justify-center py-24" style={{ color: '#78716C' }}>
              <Icon name="AlertTriangle" size={32} className="mb-3" style={{ color: '#DC2626' }} /><p>{error}</p>
            </div>
          )}
          {!loading && !error && (
            <>
              {/* Breadcrumb */}
              <div className="flex items-center gap-1.5 flex-wrap mb-4 text-xs font-medium" style={{ color: '#A8A29E' }}>
                <button onClick={() => navigate('/market-intelligence')} className="hover:underline" style={{ color: '#B45309' }}>{t('marketIntelligence.quotaTrackerNav')}</button>
                <span>/</span>
                <span style={{ color: '#57534E' }}>Statewise Allocation</span>
              </div>

              {/* Hero */}
              <div className="rounded-[18px] p-5 mb-5 relative overflow-hidden"
                style={{ background: 'linear-gradient(135deg, #0F2238 0%, #1E3A5F 55%, #B45309 130%)' }}>
                <div className="absolute -right-6 -top-6 w-32 h-32 rounded-full" style={{ background: 'rgba(245,158,11,0.15)' }} />
                <div className="flex items-start justify-between gap-3 relative">
                  <div>
                    <p className="text-xs font-bold tracking-widest mb-1" style={{ color: '#FBBF24' }}>STATEWISE ALLOCATION</p>
                    <h1 className="text-2xl font-black text-white leading-tight">
                      {sepRangeLabel ? `${sepRangeLabel} vs ${fmtMonthShort(month)}` : fmtMonthShort(month)}
                    </h1>
                    <p className="text-sm font-medium" style={{ color: '#93C5FD' }}>{rows.length} states &middot; updates automatically each fortnight</p>
                    <p className="text-xs mt-1" style={{ color: '#A8C4D8' }}>
                      {fortnight?.notificationNo ? <>Govt. Order No. <strong>{fortnight.notificationNo}</strong>{fmtNotifDate(fortnight.notificationDate) ? `, dated ${fmtNotifDate(fortnight.notificationDate)}` : ''}</> : 'Govt. order details pending'}
                    </p>
                  </div>
                  <button onClick={handleSharePDF} disabled={sharing || rows.length === 0}
                    className="flex-shrink-0 flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-[10px] disabled:opacity-50"
                    style={{ background: '#F59E0B', color: '#0F2238' }}>
                    <ShareIcon size={14} color="#0F2238" /> {sharing ? t('marketIntelligence.preparing') : t('marketIntelligence.sharePdf')}
                  </button>
                </div>
              </div>

              {/* Table - Sep 1-15 vs Aug 26 only. No Jul column, no Change column -
                  comparing a half-month fortnight against a full month by percent
                  change isn't a meaningful figure, so it's left out rather than
                  shown misleadingly. */}
              <div className="rounded-[10px] overflow-hidden mb-4" style={{ border: '1px solid #E7E0D3' }}>
                <table style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse' }}>
                  <colgroup>
                    <col style={{ width: '7%' }} />
                    <col style={{ width: '31%' }} />
                    <col style={{ width: '20%' }} />
                    <col style={{ width: '21%' }} />
                    <col style={{ width: '21%' }} />
                  </colgroup>
                  <thead>
                    <tr style={{ background: '#0F2238' }}>
                      <th className="text-center" style={{ padding: '6px 3px', fontSize: '8px', color: '#FDF8F0', textTransform: 'uppercase' }}>#</th>
                      <th className="text-left" style={{ padding: '6px 4px', fontSize: '8px', color: '#FDF8F0', textTransform: 'uppercase' }}>{t('marketplace.state', 'State')}</th>
                      <th className="text-center" style={{ padding: '6px 3px', fontSize: '8px', color: '#FDF8F0', textTransform: 'uppercase', lineHeight: 1.2 }}>{t('common.active', 'Active')}<br />{t('marketIntelligence.allFactories')}</th>
                      <th className="text-right" style={{ padding: '6px 3px', fontSize: '8px', color: '#FDF8F0', textTransform: 'uppercase', whiteSpace: 'normal', wordBreak: 'break-word' }}>{sepRangeLabel || 'Sep 1-15'}</th>
                      <th className="text-right" style={{ padding: '6px 3px', fontSize: '8px', color: '#FDF8F0', textTransform: 'uppercase' }}>{fmtMonthShort(month)}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={r.state} style={{ background: i % 2 === 0 ? '#FFFFFF' : '#FDF6E9', borderBottom: '1px solid #EDE4D3' }}>
                        <td className="text-center" style={{ padding: '5px 3px', fontSize: '9px', color: '#A8A29E' }}>{i + 1}</td>
                        <td style={{ padding: '5px 4px', fontSize: '10px', fontWeight: 700, color: '#1C1917' }}>{localizedStateName(r.state, language)}</td>
                        <td className="text-center" style={{ padding: '5px 3px', fontSize: '9px', fontWeight: 600, color: '#0F2238' }}>{r.millsActive}/{r.millsReporting}</td>
                        <td className="text-right" style={{ padding: '5px 3px', fontSize: '10px', fontWeight: 700, color: '#B45309' }}>{fmtNum(r.sep)}</td>
                        <td className="text-right" style={{ padding: '5px 3px', fontSize: '10px', color: '#1C1917' }}>{fmtNum(r.aug)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ background: '#F59E0B' }}>
                      <td style={{ padding: '7px 3px' }}></td>
                      <td style={{ padding: '7px 4px', fontSize: '10px', fontWeight: 800, color: '#0F2238' }}>{t('marketIntelligence.total', 'Total')}</td>
                      <td className="text-center" style={{ padding: '7px 3px', fontSize: '9px', fontWeight: 800, color: '#0F2238' }}>{totals.millsActive}/{totals.millsReporting}</td>
                      <td className="text-right" style={{ padding: '7px 3px', fontSize: '10px', fontWeight: 800, color: '#0F2238' }}>{fmtNum(totals.sep)}</td>
                      <td className="text-right" style={{ padding: '7px 3px', fontSize: '10px', fontWeight: 800, color: '#0F2238' }}>{fmtNum(totals.aug)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Zero MT callout - matches the email exactly */}
              <div className="rounded-[10px] mb-6" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', padding: '10px 14px' }}>
                <p className="text-xs font-bold mb-0.5" style={{ color: '#b91c1c' }}>&#9888; Factories with Zero MT Allocation</p>
                <p className="text-lg font-black" style={{ color: '#b91c1c' }}>{zeroMtCount} of {totals.millsReporting} factories</p>
              </div>

              {/* SugarHathi summary */}
              <div className="rounded-[14px] p-5 mb-6" style={{ background: '#E7F7EF', border: '1px solid #B7E8CE' }}>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(180deg, #F59E0B 0%, #D97706 100%)' }}><Icon name="Zap" size={14} color="#FFFFFF" /></div>
                  <span className="text-sm font-bold" style={{ color: '#0F2238' }}>{t('mandiFactory.summaryTitle')}</span>
                </div>
                <p className="text-sm leading-relaxed" style={{ color: '#365314' }}>
                  {sepRangeLabel ? (
                    <>National allocation for {sepRangeLabel} stood at <strong>{fmtNum(totals.sep)} MT</strong> across {rows.length} states.</>
                  ) : (
                    <>No fortnight data published yet.</>
                  )}
                  {' '}{fmtMonth(month)} ({t('marketIntelligence.completeMonthNote', 'Last complete month')}) stood at <strong>{fmtNum(totals.aug)} MT</strong>.
                  {' '}{totals.millsActive} of {totals.millsReporting} factories were active in {fmtMonth(month)}, leaving {zeroMtCount} at zero allocation.
                </p>
              </div>

              <p className="text-xs mb-8" style={{ color: '#A8A29E' }}>
                {sepRangeLabel ? `Figures reflect ${sepRangeLabel} vs ${fmtMonth(month)} (last complete month)` : `Figures reflect ${fmtMonth(month)} allocation`}, computed by SugarHathi from published government release order data &mdash; not an official ranking. A half-month fortnight isn't compared as a percent change against a full month, since that wouldn't be a meaningful figure.
              </p>

              {/* Keep exploring - footer buttons */}
              <p className="text-sm font-bold mb-2" style={{ color: '#0F2238' }}>{t('marketIntelligence.keepExploring')}</p>
              <div className="grid grid-cols-2 gap-3 mb-8">
                <button onClick={() => navigate('/market-intelligence')} className="rounded-[14px] p-4 text-left transition-transform hover:scale-[1.02]" style={{ background: '#0F2238' }}>
                  <Icon name="Home" size={18} color="#F59E0B" className="mb-2" />
                  <p className="text-sm font-bold text-white">{t('marketIntelligence.quotaTrackerHome')}</p>
                </button>
                <button onClick={() => navigate('/market-intelligence/states')} className="rounded-[14px] p-4 text-left transition-transform hover:scale-[1.02]" style={{ background: '#4338CA' }}>
                  <Icon name="Map" size={18} color="#FFFFFF" className="mb-2" />
                  <p className="text-sm font-bold text-white">{t('marketplace.allStates', 'All states')}</p>
                </button>
                <button onClick={() => navigate('/market-intelligence/districts')} className="rounded-[14px] p-4 text-left transition-transform hover:scale-[1.02]" style={{ background: '#B45309' }}>
                  <Icon name="MapPin" size={18} color="#FFFFFF" className="mb-2" />
                  <p className="text-sm font-bold text-white">{t('marketIntelligence.allDistricts')}</p>
                </button>
                <button onClick={() => navigate('/market-intelligence/compare')} className="rounded-[14px] p-4 text-left transition-transform hover:scale-[1.02]" style={{ background: '#15803D' }}>
                  <Icon name="GitCompare" size={18} color="#FFFFFF" className="mb-2" />
                  <p className="text-sm font-bold text-white">{t('marketIntelligence.compareFactories')}</p>
                </button>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default StatewiseAllocationPage;
